import { useMarketLocationId } from '@e2m-customer-portal/hooks';
import { Loading } from '@e2m-customer-portal/ui';
import { ReactElement, useMemo, useRef } from 'react';
import { useToggle } from 'react-use';
import { GroupedVirtuoso } from 'react-virtuoso';
import { SidebarMarketLocationFieldsFragment } from '../../../generated/graphql';
import { MarketLocationItem } from '../market-location-item/MarketLocationItem';

export interface MarketLocationsProps {
  marketLocations: Array<SidebarMarketLocationFieldsFragment>;
  loading: boolean;
}

export function MarketLocations({
  marketLocations,
  loading,
}: Readonly<MarketLocationsProps>): ReactElement<MarketLocationsProps> | null {
  const selectedId = useMarketLocationId();
  const expandedState = useRef(new Set<string>(selectedId ? [selectedId] : []));
  const [, trigger] = useToggle(false);
  const isExpanded = (id: string): boolean => expandedState.current.has(id);
  const setExpanded =
    (id: string) =>
    (expanded: boolean): void => {
      if (expanded !== expandedState.current.has(id)) {
        trigger();
        if (expanded) {
          expandedState.current.add(id);
        } else {
          expandedState.current.delete(id);
        }
      }
    };

  const sortedMarketLocations = useMemo(
    () =>
      new Array(...marketLocations).sort(
        (a: SidebarMarketLocationFieldsFragment, b: SidebarMarketLocationFieldsFragment) => {
          if (a.contractPartner === b.contractPartner) return 0;
          return a.contractPartner > b.contractPartner ? 1 : -1;
        },
      ),
    [marketLocations],
  );

  const allContractPartners = sortedMarketLocations.map(
    (malo: SidebarMarketLocationFieldsFragment) => malo.contractPartner,
  );
  const partnerGroups = Array.from(new Set(allContractPartners));
  const groupCounts = useMemo(() => {
    const counts: Array<number> = [];
    for (const contractPartner of partnerGroups) {
      counts.push(allContractPartners.filter((partner: string) => contractPartner === partner).length);
    }
    return counts;
  }, [allContractPartners, partnerGroups]);

  const itemContent = (index: number): ReactElement => (
    <MarketLocationItem
      key={sortedMarketLocations[index].id}
      marketLocation={sortedMarketLocations[index]}
      expanded={isExpanded(sortedMarketLocations[index].id)}
      onChangeExpanded={setExpanded(sortedMarketLocations[index].id)}
    />
  );

  const groupContent = (groupIndex: number): ReactElement => (
    <div className="bg-white px-5 py-3.5">{partnerGroups[groupIndex]}</div>
  );

  if (loading) {
    return <Loading />;
  }

  const selectedIndex = sortedMarketLocations.findIndex(
    (marketLocation: SidebarMarketLocationFieldsFragment) => marketLocation.id === selectedId,
  );

  return (
    <GroupedVirtuoso
      role="datalist"
      className="overflow-x-clip"
      defaultItemHeight={60}
      groupCounts={groupCounts}
      groupContent={groupContent}
      itemContent={itemContent}
      initialTopMostItemIndex={selectedIndex}
      scrollIntoViewOnChange={() => ({
        align: 'start',
        index: selectedIndex,
      })}
    />
  );
}
